import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchWsTicket } from './api'

/**
 * Warstwa realtime collab (STOMP-over-WebSocket).
 *
 * Model auth: przed każdym (re)connect pobieramy jednorazowy ticket przez REST
 * (POST /api/collab/ws-ticket, cookie-authed via withCredentials). Ticket jest
 * przekazywany jako natywny header STOMP CONNECT (`ticket`). Cookie NIE jest
 * wysyłane na cross-origin WS handshake - stąd ticket jako mechanizm auth.
 *
 * Jedno połączenie na cały lifetime providera (montowany raz w App, wewnątrz
 * boundary zalogowanego użytkownika). Subskrypcja per-user `/user/queue/collab`
 * (powiadomienia) jest zawsze aktywna; panele dokładają subskrypcje
 * `/topic/collab.{entityRef}` na tym samym kliencie.
 */

export type CollabEvent = {
  type: 'message.created' | 'message.updated' | 'message.deleted' | 'mention.created'
  entityRef: string
  threadId: number
  recipientUserIds?: number[]
  message?: unknown
}

type EntityCallback = (ev: CollabEvent) => void

type CollabRealtimeContextValue = {
  connected: boolean
  /** Subskrybuje `/topic/collab.{entityRef}`; zwraca funkcję odsubskrybowującą. */
  subscribeEntity: (entityRef: string, cb: EntityCallback) => () => void
  /** Unieważnia query collab powiązane z danym entityRef / wątkiem. */
  invalidateEntity: (entityRef: string, threadId: number) => void
}

const CollabRealtimeContext = createContext<CollabRealtimeContextValue | null>(null)

function deriveBrokerURL(): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8999'
  // https -> wss, http -> ws
  const wsBase = base.replace(/^http/, 'ws')
  return `${wsBase.replace(/\/$/, '')}/ws`
}

export function CollabRealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [connected, setConnected] = useState(false)

  const clientRef = useRef<Client | null>(null)
  // Aktywne subskrypcje entityRef: trzymamy callbacki, żeby przy reconnect
  // odtworzyć subskrypcje STOMP. Wiele paneli na ten sam entityRef możliwe -> Set.
  const entityCallbacksRef = useRef<Map<string, Set<EntityCallback>>>(new Map())
  const entitySubsRef = useRef<Map<string, StompSubscription>>(new Map())

  // Trzymamy stabilną referencję do qc dla handlerów wewnątrz klienta.
  const qcRef = useRef(qc)
  qcRef.current = qc

  useEffect(() => {
    const client = new Client({
      brokerURL: deriveBrokerURL(),
      reconnectDelay: 5000,
      onConnect: () => {
        setConnected(true)

        // Powiadomienia per-user (zawsze dozwolone).
        client.subscribe('/user/queue/collab', (msg: IMessage) => {
          const ev = parseEvent(msg)
          if (ev) handleNotification(ev, qcRef.current)
        })

        // Odtworzenie subskrypcji entityRef po (re)connect.
        for (const entityRef of entityCallbacksRef.current.keys()) {
          subscribeTopic(entityRef)
        }
      },
      onWebSocketClose: () => {
        setConnected(false)
        // STOMP suby są nieważne po rozłączeniu; wyczyść mapę, odtworzymy onConnect.
        entitySubsRef.current.clear()
      },
      onStompError: () => {
        setConnected(false)
      },
    })

    function subscribeTopic(entityRef: string) {
      if (entitySubsRef.current.has(entityRef)) return
      const sub = client.subscribe(`/topic/collab.${entityRef}`, (msg: IMessage) => {
        const ev = parseEvent(msg)
        if (!ev) return
        const cbs = entityCallbacksRef.current.get(entityRef)
        if (cbs) for (const cb of cbs) cb(ev)
      })
      entitySubsRef.current.set(entityRef, sub)
    }

    // Udostępniamy subscribeTopic przez ref klienta + lokalny closure niżej.
    clientRef.current = client
    subscribeTopicRef.current = subscribeTopic

    // Pobieramy świeży ticket przed każdym (re)connect i ustawiamy go jako
    // natywny header STOMP CONNECT. Tickety są jednorazowe - stąd fetch per-connect.
    client.beforeConnect = async () => {
      try {
        const ticket = await fetchWsTicket()
        client.connectHeaders = { ticket }
      } catch {
        // Brak ticketu (np. niezalogowany) -> puste headery; CONNECT się nie powiedzie
        // i stompjs ponowi próbę po reconnectDelay.
        client.connectHeaders = {}
      }
    }

    client.activate()

    return () => {
      subscribeTopicRef.current = null
      clientRef.current = null
      entitySubsRef.current.clear()
      void client.deactivate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Closure-bridge: subscribeEntity (z context) musi sięgnąć do subscribeTopic
  // zdefiniowanego wewnątrz effectu. Trzymamy go w refie.
  const subscribeTopicRef = useRef<((entityRef: string) => void) | null>(null)

  const value = useMemo<CollabRealtimeContextValue>(() => ({
    connected,
    subscribeEntity: (entityRef, cb) => {
      let set = entityCallbacksRef.current.get(entityRef)
      if (!set) {
        set = new Set()
        entityCallbacksRef.current.set(entityRef, set)
      }
      set.add(cb)

      // Jeśli klient już połączony, subskrybuj od razu; inaczej onConnect zrobi to za nas.
      if (clientRef.current?.connected) {
        subscribeTopicRef.current?.(entityRef)
      }

      return () => {
        const s = entityCallbacksRef.current.get(entityRef)
        if (s) {
          s.delete(cb)
          if (s.size === 0) {
            entityCallbacksRef.current.delete(entityRef)
            const sub = entitySubsRef.current.get(entityRef)
            if (sub) {
              sub.unsubscribe()
              entitySubsRef.current.delete(entityRef)
            }
          }
        }
      }
    },
    invalidateEntity: (entityRef, threadId) => {
      const client = qcRef.current
      client.invalidateQueries({ queryKey: ['collab', 'messages', threadId] })
      client.invalidateQueries({ queryKey: ['collab', 'threads', entityRef] })
      client.invalidateQueries({ queryKey: ['collab', 'unread', entityRef] })
    },
  }), [connected])

  return (
    <CollabRealtimeContext.Provider value={value}>
      {children}
    </CollabRealtimeContext.Provider>
  )
}

function parseEvent(msg: IMessage): CollabEvent | null {
  try {
    return JSON.parse(msg.body) as CollabEvent
  } catch {
    return null
  }
}

function handleNotification(ev: CollabEvent, qc: ReturnType<typeof useQueryClient>) {
  if (ev.type === 'mention.created') {
    toast('Nowa wzmianka')
  } else if (ev.type === 'message.created') {
    toast('Nowa wiadomość')
  }
  qc.invalidateQueries({ queryKey: ['collab', 'mentions'] })
  qc.invalidateQueries({ queryKey: ['collab', 'unread', ev.entityRef] })
}

export function useCollabRealtimeContext(): CollabRealtimeContextValue {
  const ctx = useContext(CollabRealtimeContext)
  if (!ctx) {
    throw new Error('useCollabRealtimeContext must be used within CollabRealtimeProvider')
  }
  return ctx
}

/**
 * Subskrybuje realtime dla danego `entityRef` i unieważnia odpowiednie query
 * przy każdym evencie. Zwraca `{ live }` (czy połączenie WS jest aktywne),
 * którego panel używa do wyłączenia pollingu.
 *
 * Bezpieczne wywołanie poza providerem (zwraca live=false) - dzięki temu panele
 * collab działają nawet gdy provider nie jest zamontowany (np. w testach).
 */
export function useCollabRealtime(entityRef: string): { live: boolean } {
  const ctx = useContext(CollabRealtimeContext)
  // Brak providera (np. w testach z zamockowanymi hookami) -> no-op, live=false.
  // Nie wolajemy useQueryClient bezposrednio - invalidacje robi provider, ktory
  // ma dostep do QueryClient. Dzieki temu hook dziala bez QueryClientProvider.

  useEffect(() => {
    if (!ctx) return
    return ctx.subscribeEntity(entityRef, (ev) => {
      ctx.invalidateEntity(entityRef, ev.threadId)
    })
  }, [ctx, entityRef])

  return { live: ctx?.connected ?? false }
}
